import { z } from 'zod';
import {
  loanFundingSchema,
  loanTermsFields,
  refineLoanTerms,
  LOAN_PAYMENT_KINDS,
  type AmortRow,
  type BusinessDayRule,
  type LoanPaymentKind,
  type PaymentFrequency,
  type RateBasis,
} from './loan.schemas.js';

// A loan the company makes TO someone (e.g. a shareholder): principal is a
// receivable asset; repayments reduce that asset and the interest portion is
// income. Same term model and engine as `loans`, sides inverted.

const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'expected YYYY-MM-DD');
const nonNegMoney = z.string().regex(/^\d+(\.\d{1,4})?$/, 'must be a non-negative money value');
const positiveMoney = nonNegMoney.refine((v) => Number(v) > 0, 'must be greater than zero');

export const SHAREHOLDER_LOAN_STATUSES = ['active', 'paid_off'] as const;
export type ShareholderLoanStatus = (typeof SHAREHOLDER_LOAN_STATUSES)[number];

export const createShareholderLoanInputSchema = z
  .object({
    name: z.string().trim().min(1).max(120),
    borrowerContactId: z.string().uuid().nullable().optional(),
    /** The Notes/Loans Receivable asset account holding the outstanding balance. */
    receivableAccountId: z.string().uuid(),
    /** The income account the interest portion of each repayment is booked to. */
    interestIncomeAccountId: z.string().uuid(),
    /** Bank account scheduled repayments are received into. Required when autoPostPayments. */
    depositAccountId: z.string().uuid().nullable().optional(),
    autoPostPayments: z.boolean().default(false),
    /**
     * consolidate: existing receivable balances rolled into this note (CR each);
     * advances: new money paid out of a bank account (CR each). DR the receivable.
     */
    funding: loanFundingSchema.optional(),
    ...loanTermsFields,
  })
  .superRefine((v, ctx) => {
    refineLoanTerms(v, ctx);
    if (v.autoPostPayments && !v.depositAccountId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['depositAccountId'],
        message: 'Automatic posting needs a bank account to receive into',
      });
    }
  });
export type CreateShareholderLoanInput = z.input<typeof createShareholderLoanInputSchema>;

export const recordShareholderLoanPaymentInputSchema = z.object({
  paymentDate: isoDate,
  kind: z.enum(LOAN_PAYMENT_KINDS).default('scheduled'),
  amount: positiveMoney.optional(),
  /** Bank/cash account the repayment is received into. */
  depositAccountId: z.string().uuid(),
  memo: z.string().max(200).nullable().optional(),
});
export type RecordShareholderLoanPaymentInput = z.input<typeof recordShareholderLoanPaymentInputSchema>;

export interface ShareholderLoan {
  id: string;
  companyId: string;
  name: string;
  borrowerContactId: string | null;
  receivableAccountId: string;
  interestIncomeAccountId: string;
  depositAccountId: string | null;
  autoPostPayments: boolean;
  originationJournalEntryId: string | null;
  principal: string;
  annualRatePct: string;
  rateBasis: RateBasis;
  paymentFrequency: PaymentFrequency;
  amortizationPeriods: number;
  numberOfPayments: number;
  paymentAmount: string;
  originationDate: string;
  firstPaymentDate: string;
  maturityDate: string;
  businessDayRule: BusinessDayRule;
  lateChargePct: string | null;
  lateChargeGraceDays: number | null;
  defaultRateSpreadPct: string | null;
  currentBalance: string;
  periodsPaid: number;
  status: ShareholderLoanStatus;
  nextDueDate: string | null;
  nextDueAmount: string | null;
  projectedBalloon: string;
  isOverdue: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface ShareholderLoanPaymentRecord {
  id: string;
  paymentDate: string;
  kind: LoanPaymentKind;
  scheduledPeriodNo: number | null;
  dueDate: string | null;
  accruedFrom: string | null;
  accruedTo: string | null;
  amount: string;
  principalPortion: string;
  interestPortion: string;
  lateCharge: string;
  depositAccountId: string;
  balanceAfter: string;
  journalEntryId: string | null;
  createdAt: string;
}

export interface ShareholderLoanWithDetails extends ShareholderLoan {
  schedule: AmortRow[];
  payments: ShareholderLoanPaymentRecord[];
}

export interface RecordShareholderLoanPaymentResult {
  loan: ShareholderLoan;
  kind: LoanPaymentKind;
  interest: string;
  principal: string;
  lateCharge: string;
  payment: string;
  journalEntryId: string;
}
